import { useStore } from 'vuex';
import { SetActiveOrganization, SetLastOrganizationId } from '~/constants/mutations';
import { OrgEnsured } from '~/constants/actions';
import { GetUserData } from '~/constants/getters';
import { getArcaneClaim, getOrgId, getUserId } from '~/utils/auth-claims';
import { identifyUser } from '~/composables/usePostHog';

const ARCANE_NAMESPACE = 'https://arcane.anthill.com';

const authMiddleware = async (to, from, next) => {
  const store = useStore();
  const user = store.getters['auth0Store/getAuth'].user;
  const isValid = await store.dispatch('auth0Store/isAuthenticated');

  if (!isValid) {
    next({ name: 'Error', params: { statusCode: 403 } });
    return store.getters['auth0Store/getAuth'].loginWithRedirect();
  }

  // Identity resolution lives in ~/utils/auth-claims. Three token shapes (ARP-3442,
  // ARP-3487): the Arcane bridge claims for a legacy organization on a Cloud-issued
  // token, the governance claims for a Cloud-native one, and the native `org_id` on an
  // Arcane-issued token — which is how staging and production resolve theirs today.
  //
  // So the native `org_id` IS read, but only in that third case. On a Cloud-issued
  // token it carries the Cloud Auth0 organization, which is neither identity.
  const orgId = getOrgId(user);
  if (!orgId) {
    // error is named route as we need query params
    next({ name: 'Error', params: { statusCode: 403 } });
    return Promise.resolve();
  }

  const id = getUserId(user);

  // Cloud stamps its email claim on the access token only; the ID token carries the
  // address natively under the `email` scope, so that is the fallback here.
  const email = getArcaneClaim(user, 'email') ?? user.email;
  const claimRoles = getArcaneClaim(user, 'roles') || [];

  const existingRoles = store.getters[`authStore/${GetUserData}`]?.roles || [];
  const roles = Array.from(new Set([...existingRoles, ...claimRoles]));

  store.dispatch('authStore/setUserData', { orgId, id, email, roles });
  identifyUser(email, roles);
  store.dispatch(`authStore/${SetActiveOrganization}`, orgId);
  store.dispatch(`authStore/${SetLastOrganizationId}`, orgId);
  store.dispatch(`authStore/${OrgEnsured}`);

  next();
  return Promise.resolve();
};

export default authMiddleware;
